import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Pagination,
  IconButton,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const PRIMARY = "#CC0000";

const allNewsItems = [
  {
    date: "2024.11.1",
    category: "メディア",
    title: "MTT大会のシステム支援を実施。",
  },
  {
    date: "2024.6.10",
    category: "メディア",
    title: "ココナスポーツクラブの管理システム支援を開始。",
  },
  {
    date: "2024.04.24",
    category: "メディア",
    title: "TriArrows合同会社を設立。SNS採用事業、SNS動画制作事業、DX支援事業を開始。",
  },
];

const categories = ["All", "プレスリリース", "メディア", "お知らせ"];

const ITEMS_PER_PAGE = 5;

const NewsPage = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [page, setPage] = useState(1);

  const filteredNews = useMemo(() => {
    if (activeCategory === "All") return allNewsItems;
    return allNewsItems.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  const pagedNews = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredNews.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredNews, page]);

  const pageCount = Math.max(1, Math.ceil(filteredNews.length / ITEMS_PER_PAGE));

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setPage(1);
  };

  return (
    <Box>
      {/* Page Hero */}
      <Box sx={{ bgcolor: PRIMARY, color: "white", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            sx={{ fontSize: "0.85rem", fontWeight: 600, letterSpacing: 3, mb: 2, opacity: 0.85 }}
          >
            News
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontWeight: 900, fontSize: { xs: "2rem", md: "3rem" } }}
          >
            お知らせ
          </Typography>
        </Container>
      </Box>

      {/* News Content */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          {/* Category Filter */}
          <Box sx={{ display: "flex", gap: 1.5, mb: 6, flexWrap: "wrap" }}>
            {categories.map((category) => {
              const active = activeCategory === category;
              return (
                <Button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  variant={active ? "contained" : "outlined"}
                  sx={
                    active
                      ? {
                          bgcolor: PRIMARY,
                          color: "white",
                          borderRadius: 0,
                          fontWeight: 700,
                          "&:hover": { bgcolor: "#aa0000" },
                        }
                      : {
                          color: "#555",
                          borderColor: "#ccc",
                          borderRadius: 0,
                          fontWeight: 500,
                          "&:hover": { borderColor: PRIMARY, color: PRIMARY },
                        }
                  }
                >
                  {category}
                </Button>
              );
            })}
          </Box>

          {/* News List */}
          <Box>
            {pagedNews.map((news, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 3,
                  borderBottom: "1px solid #e0e0e0",
                  "&:first-of-type": { borderTop: "1px solid #e0e0e0" },
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#fafafa" },
                  px: 1,
                  transition: "background 0.15s",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
                  <Typography
                    sx={{ fontSize: "0.85rem", color: "#888", minWidth: 90 }}
                  >
                    {news.date}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      bgcolor: PRIMARY,
                      color: "white",
                      px: 1.5,
                      py: 0.3,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    {news.category}
                  </Typography>
                  <Typography sx={{ fontSize: "0.95rem" }}>{news.title}</Typography>
                </Box>
                <IconButton size="small" sx={{ color: PRIMARY, ml: 1, flexShrink: 0 }}>
                  <ArrowForwardIosIcon fontSize="inherit" />
                </IconButton>
              </Box>
            ))}
          </Box>

          {filteredNews.length === 0 && (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography sx={{ color: "#888" }}>該当するお知らせはありません。</Typography>
            </Box>
          )}

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, value) => setPage(value)}
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  bgcolor: PRIMARY,
                  color: "white",
                  "&:hover": { bgcolor: "#aa0000" },
                },
                "& .MuiPaginationItem-root": {
                  borderRadius: 0,
                },
              }}
            />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default NewsPage;
